import { HttpErrorResponse } from '@angular/common/http';
import { Component, computed, inject, output, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  BackendValidationError,
  ErrorKeys,
  ErrorMessagesComponent,
  ErrorStateHighlightDirective,
  MarkFormTouchedDirective,
  PasswordService,
} from '@gb-webapp/gb-app';
import { TranslatePipe } from '@ngx-translate/core';
import { finalize, map, Observable, of, tap } from 'rxjs';

@Component({
  selector: 'app-forgot-password',
  imports: [
    ReactiveFormsModule,
    MarkFormTouchedDirective,
    ErrorMessagesComponent,
    ErrorStateHighlightDirective,
    TranslatePipe,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
})
export class ForgotPasswordComponent {
  forgotPasswordForm = new FormGroup({
    email: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.email,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
      ],
    }),
  });

  isSubmitting = signal(false);

  // Centralized validation rules object
  fieldValidationRules = {
    email: {
      required: 'authentication.email_required',
      email: 'authentication.pattern_error',
      pattern: 'authentication.pattern_error',
    },
  };

  public goToSignIn = output();

  public forgotPasswordRequested = output<{ email: string }>();

  protected readonly passwordService = inject(PasswordService);

  public serverValidation = signal({ [ErrorKeys.ServerValidation]: '' });

  public serverValidationMessage = computed(() => {
    return this.serverValidation()[ErrorKeys.ServerValidation] || '';
  });

  /**
   * Handles forgot password form submission with validation and API call.
   */
  submitForgotPassword(): Observable<boolean> {
    if (this.isSubmitting()) {
      return of(false);
    }

    this.isSubmitting.set(true);

    return this.passwordService
      .requestResetPassword(this.forgotPasswordForm.getRawValue().email)
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .pipe(
        map(() => {
          this.forgotPasswordRequested.emit({ email: this.forgotPasswordForm.getRawValue().email });
          return true;
        }),
        tap({
          error: (err) => {
            if (err instanceof HttpErrorResponse) {
              this.serverValidation.set({
                [ErrorKeys.ServerValidation]: err.error?.message || 'authentication.error_occurred',
              });
              this.forgotPasswordForm.setValidators([
                BackendValidationError(ErrorKeys.ServerValidation),
              ]);
              this.forgotPasswordForm.updateValueAndValidity();
            }
          },
        })
      );
  }
}
